"use client";

import { useState, useEffect, useRef } from "react";
import { useAppStore } from "@/store/app-store";
import { cn } from "@/lib/utils";
import {
  X,
  Settings,
  Users,
  Shield,
  Smile,
  Link,
  Ban,
  Trash2,
  Camera,
  ChevronRight,
  Bell,
  Hash,
  Sparkles,
} from "lucide-react";

interface ServerSettingsModalProps {
  server: {
    id: string;
    name: string;
    description?: string | null;
    icon_url?: string | null;
    banner_url?: string | null;
    is_public?: boolean;
  };
  onClose: () => void;
  onServerUpdate?: (updatedServer: any) => void;
}

type SettingsTab = "overview" | "roles" | "emoji" | "moderation" | "invites" | "bans" | "danger";

const settingsTabs = [
  { id: "overview" as const, label: "Overview", icon: Settings },
  { id: "roles" as const, label: "Roles", icon: Shield },
  { id: "emoji" as const, label: "Emoji", icon: Smile },
  { id: "moderation" as const, label: "Moderation", icon: Shield },
  { id: "invites" as const, label: "Invites", icon: Link },
  { id: "bans" as const, label: "Bans", icon: Ban },
  { id: "danger" as const, label: "Delete Server", icon: Trash2, danger: true },
];

export function ServerSettingsModal({ server, onClose, onServerUpdate }: ServerSettingsModalProps) {
  const { updateServer } = useAppStore();
  const [activeTab, setActiveTab] = useState<SettingsTab>("overview");
  const [serverName, setServerName] = useState(server.name);
  const [serverDescription, setServerDescription] = useState(server.description || "");
  const [isPublic, setIsPublic] = useState(server.is_public || false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [roles, setRoles] = useState<any[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  
  // Role editing state
  const [editingRole, setEditingRole] = useState<any | null>(null);
  const [roleMembers, setRoleMembers] = useState<any[]>([]);
  const [serverMembers, setServerMembers] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<Record<string, number>>({});

  useEffect(() => {
    loadRoles();
    loadInvites();
    loadServerMembers();
  }, [server.id]);

  const loadRoles = async () => {
    try {
      const res = await fetch(`/api/servers/${server.id}/roles`);
      if (res.ok) {
        const data = await res.json();
        setRoles(data.roles || []);
        if (data.permissions) setPermissions(data.permissions);
      }
    } catch (err) {
      console.error("Load roles error:", err);
    }
  };

  const loadServerMembers = async () => {
    try {
      const res = await fetch(`/api/servers/${server.id}/members`);
      if (res.ok) {
        const data = await res.json();
        setServerMembers(data.members || []);
      }
    } catch (err) {
      console.error("Load members error:", err);
    }
  };

  const loadRoleMembers = async (roleId: string) => {
    try {
      const res = await fetch(`/api/servers/${server.id}/roles/${roleId}/members`);
      if (res.ok) {
        const data = await res.json();
        setRoleMembers(data.members || []);
      }
    } catch (err) {
      console.error("Load role members error:", err);
    }
  };

  const handleEditRole = async (role: any) => {
    setEditingRole(role);
    await loadRoleMembers(role.id);
  };

  const handleSaveRole = async () => {
    if (!editingRole) return;
    try {
      const res = await fetch(`/api/servers/${server.id}/roles/${editingRole.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editingRole.name,
          color: editingRole.color,
          permissions: editingRole.permissions,
          hoist: editingRole.hoist,
          mentionable: editingRole.mentionable,
        }),
      });
      if (res.ok) {
        setSuccess("Role updated!");
        setTimeout(() => setSuccess(null), 2000);
        loadRoles();
        setEditingRole(null);
      }
    } catch (err) {
      setError("Failed to save role");
    }
  };

  const handleAddMemberToRole = async (userId: string) => {
    if (!editingRole) return;
    try {
      const res = await fetch(`/api/servers/${server.id}/roles/${editingRole.id}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      if (res.ok) {
        loadRoleMembers(editingRole.id);
        loadRoles();
      }
    } catch (err) {
      console.error("Add member to role error:", err);
    }
  };

  const handleRemoveMemberFromRole = async (userId: string) => {
    if (!editingRole) return;
    try {
      const res = await fetch(`/api/servers/${server.id}/roles/${editingRole.id}/members`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      if (res.ok) {
        loadRoleMembers(editingRole.id);
        loadRoles();
      }
    } catch (err) {
      console.error("Remove member from role error:", err);
    }
  };

  const togglePermission = (permName: string) => {
    if (!editingRole) return;
    const permValue = permissions[permName] || 0;
    const currentPerms = parseInt(editingRole.permissions) || 0;
    const hasPermission = (currentPerms & permValue) !== 0;
    const newPerms = hasPermission ? currentPerms & ~permValue : currentPerms | permValue;
    setEditingRole({ ...editingRole, permissions: newPerms });
  };

  const hasPermission = (permName: string) => {
    if (!editingRole) return false;
    const permValue = permissions[permName] || 0;
    const currentPerms = parseInt(editingRole.permissions) || 0;
    return (currentPerms & permValue) !== 0;
  };

  const loadInvites = async () => {
    try {
      const res = await fetch(`/api/servers/${server.id}/invites`);
      if (res.ok) {
        const data = await res.json();
        setInvites(data.invites || []);
      }
    } catch (err) {
      console.error("Load invites error:", err);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/servers/${server.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: serverName, description: serverDescription }),
      });
      if (res.ok) {
        const data = await res.json();
        updateServer(server.id, { name: serverName, description: serverDescription });
        if (onServerUpdate) {
          onServerUpdate(data.server);
        }
        setHasChanges(false);
        setSuccess("Settings saved!");
        setTimeout(() => setSuccess(null), 2000);
      } else {
        const data = await res.json();
        setError(data.error || "Failed to save");
      }
    } catch (err) {
      setError("Failed to save settings");
    }
    setIsSaving(false);
  };

  const handleUploadIcon = async (file: File) => {
    const formData = new FormData();
    formData.append("icon", file);
    try {
      const res = await fetch(`/api/servers/${server.id}/icon`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setSuccess("Icon updated!");
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err) {
      setError("Failed to upload icon");
    }
  };

  const handleCreateRole = async () => {
    try {
      const res = await fetch(`/api/servers/${server.id}/roles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: "New Role" }),
      });
      if (res.ok) {
        loadRoles();
      }
    } catch (err) {
      console.error("Create role error:", err);
    }
  };

  const handleCreateInvite = async () => {
    try {
      const res = await fetch(`/api/servers/${server.id}/invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        loadInvites();
        setSuccess("Invite created!");
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err) {
      console.error("Create invite error:", err);
    }
  };

  const handleDeleteServer = async () => {
    if (!confirm(`Are you sure you want to delete ${server.name}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/servers/${server.id}`, { method: "DELETE" });
      if (res.ok) {
        onClose();
        window.location.reload();
      } else {
        const data = await res.json();
        setError(data.error || "Failed to delete server");
      }
    } catch (err) {
      setError("Failed to delete server");
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!confirm("Delete this role?")) return;
    try {
      const res = await fetch(`/api/servers/${server.id}/roles/${roleId}`, { method: "DELETE" });
      if (res.ok) {
        loadRoles();
        setSuccess("Role deleted!");
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err) {
      setError("Failed to delete role");
    }
  };

  const handleDeleteInvite = async (inviteId: string) => {
    try {
      const res = await fetch(`/api/servers/${server.id}/invites/${inviteId}`, { method: "DELETE" });
      if (res.ok) {
        loadInvites();
      }
    } catch (err) {
      console.error("Delete invite error:", err);
    }
  };

  const handleChange = () => {
    setHasChanges(true);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-6">
            {/* Server Icon */}
            <div className="flex items-start gap-6">
              <div className="relative group">
                <div className="w-24 h-24 rounded-2xl bg-zinc-700 flex items-center justify-center overflow-hidden">
                  {server.icon_url ? (
                    <img src={server.icon_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl font-bold">{serverName.charAt(0)}</span>
                  )}
                </div>
                <button className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
                  <Camera className="w-6 h-6 text-white" />
                </button>
              </div>
              <div className="flex-1">
                <p className="text-sm text-zinc-400 mb-2">
                  We recommend an image of at least 512x512 for the server.
                </p>
                <button className="px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-colors text-sm font-medium">
                  Upload Image
                </button>
              </div>
            </div>

            {/* Server Name */}
            <div>
              <label className="block text-xs font-semibold uppercase text-zinc-400 mb-2">
                Server Name
              </label>
              <input
                type="text"
                value={serverName}
                onChange={(e) => { setServerName(e.target.value); handleChange(); }}
                className="w-full h-12 px-4 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 transition-colors outline-none text-white"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold uppercase text-zinc-400 mb-2">
                Server Description
              </label>
              <textarea
                value={serverDescription}
                onChange={(e) => { setServerDescription(e.target.value); handleChange(); }}
                rows={3}
                className="w-full px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 transition-colors resize-none outline-none text-white"
                placeholder="Tell people what your server is about..."
              />
            </div>

            {/* Public Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
              <div>
                <p className="font-medium">Public Server</p>
                <p className="text-sm text-zinc-400">
                  Anyone can discover and join this server
                </p>
              </div>
              <button
                onClick={() => { setIsPublic(!isPublic); handleChange(); }}
                className={cn(
                  "w-12 h-6 rounded-full transition-colors relative",
                  isPublic ? "bg-violet-600" : "bg-zinc-600"
                )}
              >
                <div
                  className={cn(
                    "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform",
                    isPublic ? "translate-x-7" : "translate-x-1"
                  )}
                />
              </button>
            </div>

            {/* System Messages Channel */}
            <div>
              <label className="block text-xs font-semibold uppercase text-zinc-400 mb-2">
                System Messages Channel
              </label>
              <button className="w-full flex items-center justify-between h-12 px-4 rounded-lg bg-zinc-900 border border-zinc-600 hover:border-violet-500/50 transition-colors">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-zinc-400" />
                  <span>general</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </button>
            </div>
          </div>
        );

      case "roles":
        // If editing a role, show the role editor
        if (editingRole) {
          return (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setEditingRole(null)}
                  className="p-2 hover:bg-zinc-700 rounded-lg"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: editingRole.color || "#99AAB5" }}
                />
                <h3 className="font-semibold text-lg">{editingRole.name}</h3>
              </div>

              {success && (
                <div className="p-3 rounded-lg bg-green-500/20 text-green-400 text-sm">{success}</div>
              )}

              {/* Role Name & Color */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-zinc-400 mb-2">Role Name</label>
                  <input
                    type="text"
                    value={editingRole.name}
                    onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                    disabled={editingRole.is_default}
                    className="w-full h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-zinc-400 mb-2">Role Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={editingRole.color || "#99AAB5"}
                      onChange={(e) => setEditingRole({ ...editingRole, color: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer bg-transparent"
                    />
                    <input
                      type="text"
                      value={editingRole.color || "#99AAB5"}
                      onChange={(e) => setEditingRole({ ...editingRole, color: e.target.value })}
                      className="flex-1 h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 outline-none font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Role Settings */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-zinc-400 uppercase">Role Settings</h4>
                <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900">
                  <div>
                    <p className="font-medium">Display separately</p>
                    <p className="text-xs text-zinc-400">Show members with this role separately in the member list</p>
                  </div>
                  <button
                    onClick={() => setEditingRole({ ...editingRole, hoist: !editingRole.hoist })}
                    className={cn("w-10 h-5 rounded-full transition-colors relative", editingRole.hoist ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform", editingRole.hoist ? "translate-x-5" : "translate-x-0.5")} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900">
                  <div>
                    <p className="font-medium">Allow anyone to @mention</p>
                    <p className="text-xs text-zinc-400">Members can mention this role</p>
                  </div>
                  <button
                    onClick={() => setEditingRole({ ...editingRole, mentionable: !editingRole.mentionable })}
                    className={cn("w-10 h-5 rounded-full transition-colors relative", editingRole.mentionable ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform", editingRole.mentionable ? "translate-x-5" : "translate-x-0.5")} />
                  </button>
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-zinc-400 uppercase">Permissions</h4>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {Object.keys(permissions).map((perm) => (
                    <div key={perm} className="flex items-center justify-between p-2 rounded bg-zinc-900">
                      <span className="text-sm">{perm.replace(/_/g, " ")}</span>
                      <button
                        onClick={() => togglePermission(perm)}
                        className={cn("w-8 h-4 rounded-full transition-colors relative", hasPermission(perm) ? "bg-violet-600" : "bg-zinc-600")}
                      >
                        <div className={cn("absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform", hasPermission(perm) ? "translate-x-4" : "translate-x-0.5")} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Members with this role */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-zinc-400 uppercase">
                    Members — {roleMembers.length}
                  </h4>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {roleMembers.map((member: any) => (
                    <div key={member.id} className="flex items-center justify-between p-2 rounded-lg bg-zinc-900">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-sm">
                          {member.display_name?.charAt(0) || "U"}
                        </div>
                        <span>{member.display_name}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveMemberFromRole(member.id)}
                        className="p-1 hover:bg-zinc-700 rounded text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Add member dropdown */}
                <select
                  onChange={(e) => { if (e.target.value) { handleAddMemberToRole(e.target.value); e.target.value = ""; } }}
                  className="w-full h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 outline-none"
                >
                  <option value="">Add member to role...</option>
                  {serverMembers
                    .filter((m: any) => !roleMembers.some((rm: any) => rm.id === m.user_id))
                    .map((m: any) => (
                      <option key={m.user_id} value={m.user_id}>{m.display_name || m.username}</option>
                    ))}
                </select>
              </div>

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-zinc-700">
                <button
                  onClick={() => setEditingRole(null)}
                  className="px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRole}
                  className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 font-medium"
                >
                  Save Changes
                </button>
              </div>
            </div>
          );
        }

        // Role list view
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Roles — {roles.length}</h3>
              <button 
                onClick={handleCreateRole}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-500 transition-colors"
              >
                Create Role
              </button>
            </div>
            
            <p className="text-sm text-zinc-400">
              Use roles to organize your server members and customize their permissions.
            </p>
            
            <div className="space-y-2">
              {roles.map((role: any) => (
                <div 
                  key={role.id}
                  onClick={() => handleEditRole(role)}
                  className="flex items-center justify-between p-3 rounded-lg bg-zinc-900 hover:bg-zinc-800 cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: role.color || "#99AAB5" }}
                    />
                    <span className="font-medium">{role.name}</span>
                    {role.is_default ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-400">Default</span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-zinc-400">{role.member_count || 0} members</span>
                    {!role.is_default && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteRole(role.id); }}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-zinc-700 rounded text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <ChevronRight className="w-4 h-4 text-zinc-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "emoji":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Custom Emoji</h3>
                <p className="text-sm text-zinc-400">0 of 50 slots used</p>
              </div>
              <button className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-500 transition-colors">
                Upload Emoji
              </button>
            </div>

            <div className="p-8 rounded-lg border-2 border-dashed border-zinc-600 text-center">
              <Smile className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
              <p className="text-zinc-400">No custom emoji yet</p>
              <p className="text-sm text-zinc-500">Upload some to get started!</p>
            </div>

            {/* Boost Info */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-violet-600/10 to-fuchsia-600/10 border border-violet-500/20">
              <div className="flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-violet-500" />
                <div>
                  <p className="font-medium">Want more emoji slots?</p>
                  <p className="text-sm text-zinc-400">
                    Boost your server to unlock up to 250 emoji slots!
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case "invites":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Server Invites</h3>
              <button 
                onClick={handleCreateInvite}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-500 transition-colors"
              >
                Create Invite
              </button>
            </div>
            
            <div className="space-y-2">
              {invites.length === 0 ? (
                <div className="text-center py-8 text-zinc-400">
                  <Link className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No active invites</p>
                </div>
              ) : (
                invites.map((invite: any) => (
                  <div key={invite.id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900">
                    <div>
                      <code className="text-violet-400">/{invite.code}</code>
                      <p className="text-xs text-zinc-400 mt-1">
                        Created by {invite.display_name || invite.username} • {invite.uses || 0} uses
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/invite/${invite.code}`);
                          setSuccess("Link copied!");
                          setTimeout(() => setSuccess(null), 2000);
                        }}
                        className="px-3 py-1 rounded bg-zinc-700 hover:bg-zinc-600 text-sm"
                      >
                        Copy
                      </button>
                      <button 
                        onClick={() => handleDeleteInvite(invite.id)}
                        className="px-3 py-1 rounded bg-red-600/20 hover:bg-red-600/40 text-red-400 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case "danger":
        return (
          <div className="space-y-6">
            <div className="p-6 rounded-xl border-2 border-red-500/30 bg-red-500/5">
              <h3 className="text-lg font-semibold text-red-500 mb-2">Delete Server</h3>
              <p className="text-sm text-zinc-400 mb-4">
                Are you sure you want to delete <strong>{server.name}</strong>? This action cannot be undone.
              </p>
              <button 
                onClick={handleDeleteServer}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-500 transition-colors"
              >
                Delete Server
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-64 text-zinc-400">
            <p>This section is coming soon</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-4xl h-[600px] bg-zinc-800 rounded-2xl overflow-hidden shadow-2xl flex">
        {/* Sidebar */}
        <div className="w-56 bg-zinc-900 border-r border-zinc-700 flex flex-col">
          <div className="p-4 border-b border-zinc-700">
            <h2 className="font-semibold truncate">{server.name}</h2>
            <p className="text-xs text-zinc-400">Server Settings</p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2">
            {settingsTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors",
                    activeTab === tab.id
                      ? "bg-zinc-700 text-white"
                      : tab.danger
                        ? "text-red-500 hover:bg-red-500/10"
                        : "text-zinc-400 hover:text-white hover:bg-zinc-700/50"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="h-14 px-6 flex items-center justify-between border-b border-zinc-700">
            <h3 className="font-semibold capitalize">{activeTab}</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-700 transition-colors text-zinc-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Toast Messages */}
          {error && (
            <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-10">
              {error}
              <button onClick={() => setError(null)} className="ml-2 font-bold">×</button>
            </div>
          )}
          {success && (
            <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-10">
              {success}
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderContent()}
          </div>

          {/* Save Bar */}
          {hasChanges && (
            <div className="p-4 bg-zinc-900 border-t border-zinc-700 flex items-center justify-between">
              <p className="text-sm text-zinc-400">Careful — you have unsaved changes!</p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setServerName(server.name);
                    setServerDescription(server.description || "");
                    setIsPublic(server.is_public || false);
                    setHasChanges(false);
                  }}
                  className="px-4 py-2 rounded-lg hover:underline text-sm font-medium"
                >
                  Reset
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-500 transition-colors disabled:opacity-50"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RoleItem({ 
  name, 
  color, 
  members, 
  isDefault,
  onEdit,
  onDelete
}: { 
  name: string; 
  color: string; 
  members: number;
  isDefault?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-700 transition-colors text-left group">
      <div 
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: color }}
      />
      <div className="flex-1">
        <p className="font-medium">{name}</p>
        <p className="text-xs text-zinc-400">{members} members</p>
      </div>
      {!isDefault && (
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <button onClick={onEdit} className="p-1 hover:bg-zinc-600 rounded" title="Edit">
              <Settings className="w-4 h-4 text-zinc-400" />
            </button>
          )}
          {onDelete && (
            <button onClick={onDelete} className="p-1 hover:bg-red-500/20 rounded" title="Delete">
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
